<template>
  <FinancialsOverviewCard
    title="Suggested Monthly Payment"
    :loading="isLoading"
    expandable
  >
    <template #default>
      <h4 v-if="hasMeterpoints" class="d-flex text-truncate">
        <span
          data-testid="suggested-payment-formatted"
          :class="isMobile ? 'text-h5' : 'text-h4'"
          >{{ suggestedPaymentFormatted }}</span
        >
        <span
          v-if="paymentDifference"
          class="ml-1"
          :class="getPaymentDifferenceClass"
        >
          {{ paymentDifferenceFormatted }}
          {{ paymentDifference > 0 ? "↑" : "	↓" }}
        </span>
      </h4>
      <h4 v-else :class="isMobile ? 'text-h5' : 'text-h4'">
        No meterpoints found
      </h4>
    </template>
    <template #details>
      <SoCheckbox v-model="isSeasonal" label="Show As Seasonal" />
      <div class="d-flex align-center gap-1">
        <SoCheckbox
          v-model="includeAccountBalance"
          label="Include Account Balance"
          :disabled="hasActiveDebtPaymentPlan"
          data-dd-action-name="includeAccountBalanceCheckbox"
        />
        <SoTooltip
          v-if="hasActiveDebtPaymentPlan"
          text="
            This customer has an agreed payment plan which should take care of their existing
            debit balance, allowing you to focus the DD review only on their usage. If their
            debit balance is still increasing, you may need to review the payment plan and
            agree a new one or cancel the existing payment plan and include their account
            balance into the DD review calculations."
          data-testid="tooltip-debt-payment-plan-active"
        >
          <SoIcon icon="help-circle" size="small" />
        </SoTooltip>
      </div>

      <SoCheckbox
        v-model="includeUnbilledReadings"
        label="Include Unbilled Readings"
        :disabled="!includeAccountBalance"
        data-dd-action-name="includeUnbilledReadsCheckbox"
      />
      <br />
      <SoButton
        v-if="!isDirectDebitReview && current.account.data.isActive"
        title="Review Payment Amount"
        icon="arrow-up-right"
        class="mb-1"
        @click="$router.push({ name: RouteNames.CustomerReviewPaymentAmount })"
      />
      <SoAlert v-else-if="!current.account.data.isActive">
        Unable to review payment amount for
        {{ current.account.data?.status.text.toLowerCase() }}
        account.
      </SoAlert>
    </template>
  </FinancialsOverviewCard>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import FinancialsOverviewCard from "../components/FinancialsOverviewCard.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import { formatMoney, formatSeasonalAmount } from "@/helpers/moneyHelpers"
  import {
    SEASONAL_FACTOR_SUMMER,
    SEASONAL_FACTOR_WINTER,
  } from "@/constants/finance"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { useReviewPaymentStore } from "@/stores/reviewPayment"
  import { RouteNames } from "@/types/router"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import SoIcon from "@/components/SoIcon.vue"

  export default defineComponent({
    components: {
      FinancialsOverviewCard,
      SoCheckbox,
      SoButton,
      SoAlert,
      SoTooltip,
      SoIcon,
    },

    props: {
      useUsageAmendedMeterpoints: {
        type: Boolean,
        default: false,
      },
      isDirectDebitReview: {
        type: Boolean,
        default: false,
      },
    },

    data() {
      return {
        current: useCurrentAccountStore(),
        activeDebtPaymentPlan: useCurrentAccountStore().activeDebtPaymentPlan,
        reviewPayment: useReviewPaymentStore(),
        includeAccountBalance:
          useCurrentAccountStore().suggestedPaymentParams.includeBalance,
        isSeasonal: false,
        includeUnbilledReadings: false,
        formatMoney,
        formatSeasonalAmount,
        RouteNames,
      }
    },

    computed: {
      hasActiveDebtPaymentPlan() {
        return !!this.activeDebtPaymentPlan.data
      },
      suggestedPayment() {
        return this.current.suggestedPaymentBreakdown(
          this.current.suggestedPaymentParams.includeBalance,
          this.includeUnbilledReadings,
        )
      },
      suggestedPaymentFormatted() {
        const amount = this.suggestedPayment.data
        return this.isSeasonal
          ? formatSeasonalAmount({
              divider: "/",
              showNegativeSign: false,
              round: false,
              seasonalAmounts: {
                winter: amount?.seasonalMonthlyPaymentWinter || 0,
                summer: amount?.seasonalMonthlyPaymentSummer || 0,
              },
            })
          : formatMoney({
              amount: amount?.recommendedMonthlyPayment || 0,
              showNegativeSign: false,
              round: false,
            })
      },
      currentPaymentSchedule() {
        return this.current.currentPaymentSchedule
      },
      accountInDebit() {
        return this.current.account.data.balance > 0
      },
      isCurrentPaymentSeasonal() {
        return !!this.currentPaymentSchedule.data?.isSeasonal
      },
      paymentDifference() {
        const currentPayment =
          this.currentPaymentSchedule.data?.averagePayment ?? 0
        const suggestedPayment =
          this.suggestedPayment.data?.recommendedMonthlyPayment ?? 0
        return suggestedPayment - currentPayment
      },
      paymentDifferenceFormatted() {
        if (this.isSeasonal) {
          const currentAverage =
            this.currentPaymentSchedule.data?.averagePayment ?? 0

          // Calculate current seasonal amounts:
          // - If current IS seasonal: apply seasonal factors
          // - If current is NOT seasonal: use flat amount for both seasons
          const currentSeasonalWinter = this.isCurrentPaymentSeasonal
            ? currentAverage * SEASONAL_FACTOR_WINTER
            : currentAverage
          const currentSeasonalSummer = this.isCurrentPaymentSeasonal
            ? currentAverage * SEASONAL_FACTOR_SUMMER
            : currentAverage

          const suggestedWinter =
            this.suggestedPayment.data?.seasonalMonthlyPaymentWinter || 0
          const suggestedSummer =
            this.suggestedPayment.data?.seasonalMonthlyPaymentSummer || 0

          return formatSeasonalAmount({
            divider: "/",
            showPositiveSign: true,
            showNegativeSign: true,
            round: true,
            seasonalAmounts: {
              winter: suggestedWinter - currentSeasonalWinter,
              summer: suggestedSummer - currentSeasonalSummer,
            },
          })
        }
        return formatMoney({
          amount: this.paymentDifference,
          showNegativeSign: true,
          showPositiveSign: true,
          round: true,
        })
      },
      isLoading() {
        return (
          this.suggestedPayment.isFetching ||
          this.currentPaymentSchedule.isFetching ||
          this.activeDebtPaymentPlan.isFetching
        )
      },
      hasMeterpoints() {
        return (
          !this.current.meterpoints.isFetching &&
          this.current.meterpoints.data.length > 0
        )
      },
      getPaymentDifferenceClass() {
        return {
          "text-success": this.paymentDifference < 0,
          "text-danger": this.paymentDifference > 0,
        }
      },
      isMobile() {
        return this.$vuetify.display.mobile
      },
    },

    watch: {
      isCurrentPaymentSeasonal(newVal: boolean) {
        if (newVal) {
          this.isSeasonal = this.isCurrentPaymentSeasonal
        }
      },
      includeAccountBalance(newVal: boolean) {
        this.current.suggestedPaymentParams.includeBalance = newVal

        if (!newVal) {
          this.includeUnbilledReadings = false
        }
      },
      hasActiveDebtPaymentPlan: {
        immediate: true,
        handler(newVal: boolean) {
          if (newVal) {
            this.includeAccountBalance = false
            this.current.suggestedPaymentParams.includeBalance = false
          }
        },
      },
    },

    created() {
      this.isSeasonal = this.isCurrentPaymentSeasonal
    },
  })
</script>
